import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  LoadingOutlined,
  ExportOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';

/**
 * The images live on the API's domain, so a plain <a download> would be cross-origin and the
 * browser would just open the image. This route sends it with Content-Disposition: attachment.
 */
export const downloadUrl = (imageUrl: string, name?: string) => {
  try {
    const path = new URL(imageUrl, window.location.origin).pathname.replace(/^\/+/, '');
    const query = new URLSearchParams({ path, ...(name ? { name } : {}) });
    return `${API_URL}/images/download?${query.toString()}`;
  } catch {
    return imageUrl;
  }
};

export interface LightboxImage {
  /** Full-size image, shown once it has loaded. */
  full: string;
  /** 800px copy, already in the browser cache from the grid — shown immediately. */
  thumb?: string;
}

/**
 * Full-screen image viewer.
 *
 * The point of it is speed: antd's preview waits for the full image before showing anything, which
 * takes seconds on a phone photo. This shows the thumbnail the grid already downloaded (scaled up,
 * slightly blurred) the instant it opens, swaps in the full image when it arrives, and preloads the
 * neighbours so paging through a feed is instant.
 */
const ImageLightbox: React.FC<{
  images: LightboxImage[];
  startIndex?: number;
  onClose: () => void;
  /** Base name for a saved file, e.g. the project and date of the post. */
  downloadName?: string;
  labels: { close: string; previous: string; next: string; openOriginal: string; download: string };
}> = ({ images, startIndex = 0, onClose, downloadName, labels }) => {
  const [index, setIndex] = useState(startIndex);
  const [fullLoaded, setFullLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const count = images.length;
  const current = images[index];

  const go = useCallback(
    (delta: number) => {
      if (count < 2) return;
      setFullLoaded(false);
      setIndex((previous) => (previous + delta + count) % count);
    },
    [count],
  );

  // Keyboard control, and keep the page behind from scrolling
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') go(1);
      if (event.key === 'ArrowLeft') go(-1);
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    containerRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [go, onClose]);

  // The next and previous full images, fetched quietly while this one is being looked at
  useEffect(() => {
    if (!fullLoaded || count < 2) return;
    [1, -1].forEach((delta) => {
      const neighbour = images[(index + delta + count) % count];
      if (neighbour) new Image().src = neighbour.full;
    });
  }, [fullLoaded, index, images, count]);

  if (!current) return null;

  return createPortal(
    <div
      className="e22-lightbox"
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
      ref={containerRef}
      onClick={onClose}
    >
      <div className="e22-lightbox-bar" onClick={(event) => event.stopPropagation()}>
        {count > 1 && (
          <span className="e22-lightbox-counter">
            {index + 1} / {count}
          </span>
        )}
        <a
          className="e22-lightbox-button"
          href={downloadUrl(current.full, downloadName ? `${downloadName}${count > 1 ? `-${index + 1}` : ''}` : undefined)}
          title={labels.download}
          aria-label={labels.download}
        >
          <DownloadOutlined />
        </a>
        <a
          className="e22-lightbox-button"
          href={current.full}
          target="_blank"
          rel="noreferrer"
          title={labels.openOriginal}
          aria-label={labels.openOriginal}
        >
          <ExportOutlined />
        </a>
        <button type="button" className="e22-lightbox-button" onClick={onClose} title={labels.close} aria-label={labels.close}>
          <CloseOutlined />
        </button>
      </div>

      {count > 1 && (
        <button
          type="button"
          className="e22-lightbox-nav e22-lightbox-nav--prev"
          onClick={(event) => {
            event.stopPropagation();
            go(-1);
          }}
          aria-label={labels.previous}
        >
          <LeftOutlined />
        </button>
      )}

      <figure className="e22-lightbox-stage" onClick={(event) => event.stopPropagation()}>
        {/* Cached thumbnail first so something is on screen immediately */}
        {current.thumb && current.thumb !== current.full && (
          <img src={current.thumb} alt="" className={`e22-lightbox-preview ${fullLoaded ? 'is-hidden' : ''}`} />
        )}
        <img
          key={current.full}
          src={current.full}
          alt=""
          className={`e22-lightbox-image ${fullLoaded ? 'is-loaded' : ''}`}
          decoding="async"
          onLoad={() => setFullLoaded(true)}
          onError={() => setFullLoaded(true)}
        />
        {!fullLoaded && (
          <span className="e22-lightbox-spinner" aria-hidden>
            <LoadingOutlined spin />
          </span>
        )}
      </figure>

      {count > 1 && (
        <button
          type="button"
          className="e22-lightbox-nav e22-lightbox-nav--next"
          onClick={(event) => {
            event.stopPropagation();
            go(1);
          }}
          aria-label={labels.next}
        >
          <RightOutlined />
        </button>
      )}
    </div>,
    document.body,
  );
};

export default ImageLightbox;
